import {
    createContext,
    useContext,
    useEffect,
    useId,
    useMemo,
    useRef,
    type KeyboardEvent,
    type MouseEvent,
} from "react";
import { isFromTextField } from "@design-system/overlay/textFields";
import { BOARD_CARD_ATTRIBUTE } from "./boardAttributes";
import type { BoardCells } from "./boardCells";
import { BoardCardKeyContext } from "./boardRoving";
import {
    extendBoardSelection,
    indexBoardEntries,
    selectBoardCard,
    type BoardSelectGesture,
    type BoardSelectResult,
} from "./boardSelection";
import {
    isFromCardControl,
    isFromInteractiveDescendant,
    isFromPortal,
    isPlaceModifier,
} from "./boardSensors";

/** A board's selection, handed to every card. */
export interface BoardSelection {
    /** The hidden "Selected" text selected cards add to their description. */
    describedById: string;
    selectedItemIds: ReadonlySet<string>;
    isItemSelected: (itemId: string) => boolean;
    isCardSelected: (placementKey: string) => boolean;
    /** Applies a ⌘/Ctrl or Shift click; false for a plain click. */
    selectFromClick: (placementKey: string, event: MouseEvent) => boolean;
    /** X on the focused card: toggles it, like a ⌘/Ctrl click. */
    toggleCard: (placementKey: string) => void;
    /** Shift+arrow moved focus between cards (`extendBoardSelection`). */
    extendTo: (fromKey: string, toKey: string) => void;
    /** ⌘/Ctrl+A: these items, the anchor dropped. */
    selectItems: (itemIds: ReadonlySet<string>) => void;
    clear: () => void;
}

export const BoardSelectionContext = createContext<BoardSelection | null>(null);

interface UseBoardSelectionArgs<TItem> {
    cells: BoardCells<TItem>;
    selectedItemIds: ReadonlySet<string> | undefined;
    onSelectionChange: ((itemIds: ReadonlySet<string>) => void) | undefined;
    announce: (message: string) => void;
}

/** The empty selection, shared so it keeps one identity. */
export const NO_ITEM_IDS: ReadonlySet<string> = new Set();

const getGesture = (event: MouseEvent): BoardSelectGesture | null => {
    if (event.shiftKey) {
        return "range";
    }
    return isPlaceModifier(event) ? "toggle" : null;
};

/**
 * The board's controlled selection: ⌘/Ctrl-click toggles a card, Shift-click
 * adds the run from the last toggled card within its cell, the keyboard does
 * the same from the focused card, and each change is announced. Null without
 * `onSelectionChange`, so the board then has no selection at all.
 */
export const useBoardSelection = <TItem>({
    cells,
    selectedItemIds = NO_ITEM_IDS,
    onSelectionChange,
    announce,
}: UseBoardSelectionArgs<TItem>): BoardSelection | null => {
    const describedById = useId();
    const anchorKeyRef = useRef<string | null>(null);
    const isSelectable = onSelectionChange !== undefined;
    const indexes = useMemo(
        () => (isSelectable ? indexBoardEntries(cells) : null),
        [cells, isSelectable],
    );
    // A range only starts from a selected card, so the anchor goes once its
    // item leaves the selection, whoever changed it.
    useEffect(() => {
        const anchorKey = anchorKeyRef.current;
        const anchorItemId =
            anchorKey === null
                ? undefined
                : indexes?.get(anchorKey)?.entry.itemId;
        if (anchorItemId === undefined || !selectedItemIds.has(anchorItemId)) {
            anchorKeyRef.current = null;
        }
    }, [indexes, selectedItemIds]);
    return useMemo(() => {
        if (onSelectionChange === undefined || indexes === null) {
            return null;
        }
        const change = (next: ReadonlySet<string>, message: string) => {
            onSelectionChange(next);
            announce(message);
        };
        const select = (next: ReadonlySet<string>) =>
            change(next, `${next.size} selected`);
        const commit = (result: BoardSelectResult | null) => {
            if (result !== null) {
                anchorKeyRef.current = result.anchorKey;
                select(result.selectedItemIds);
            }
        };
        const getCurrent = () => ({
            cells,
            indexes,
            selectedItemIds,
            anchorKey: anchorKeyRef.current,
        });
        return {
            describedById,
            selectedItemIds,
            isItemSelected: (itemId) => selectedItemIds.has(itemId),
            isCardSelected: (placementKey) => {
                const itemId = indexes.get(placementKey)?.entry.itemId;
                return itemId !== undefined && selectedItemIds.has(itemId);
            },
            selectFromClick: (placementKey, event) => {
                const gesture = getGesture(event);
                if (gesture === null) {
                    return false;
                }
                commit(
                    selectBoardCard({ ...getCurrent(), placementKey, gesture }),
                );
                return true;
            },
            toggleCard: (placementKey) =>
                commit(
                    selectBoardCard({
                        ...getCurrent(),
                        placementKey,
                        gesture: "toggle",
                    }),
                ),
            extendTo: (fromKey, toKey) =>
                commit(
                    extendBoardSelection({ ...getCurrent(), fromKey, toKey }),
                ),
            selectItems: (itemIds) => {
                anchorKeyRef.current = null;
                select(itemIds);
            },
            clear: () => {
                anchorKeyRef.current = null;
                if (selectedItemIds.size > 0) {
                    change(NO_ITEM_IDS, "Selection cleared");
                }
            },
        };
    }, [
        cells,
        indexes,
        selectedItemIds,
        onSelectionChange,
        announce,
        describedById,
    ]);
};

// Shift-click would extend the page's text selection; the card still takes
// focus, as on any other click. In a text field it extends the field's.
const keepShiftClickFocus = (event: MouseEvent<HTMLElement>) => {
    if (event.shiftKey && !isFromPortal(event) && !isFromTextField(event)) {
        event.preventDefault();
        event.currentTarget.focus({ preventScroll: true });
    }
};

/** A ⌘/Ctrl or Shift press on a control inside the card, such as a chip; not a text field. */
const isSelectPressOnControl = (event: MouseEvent<HTMLElement>) =>
    getGesture(event) !== null &&
    isFromInteractiveDescendant(event) &&
    !isFromTextField(event);

/**
 * A `BoardCard`'s part in the selection; null off a selectable board. Its
 * `pressHandlers` capture a ⌘/Ctrl or Shift press on a control in the card,
 * so the press selects the card and the control never sees it: a picker
 * trigger opens on pointer or mouse down, before the card's own handlers.
 */
export const useBoardCardSelection = () => {
    const selection = useContext(BoardSelectionContext);
    const placementKey = useContext(BoardCardKeyContext);
    if (selection === null || placementKey === null) {
        return null;
    }
    const selectFromClick = (event: MouseEvent) =>
        selection.selectFromClick(placementKey, event);
    return {
        describedById: selection.describedById,
        isSelected: selection.isCardSelected(placementKey),
        selectFromClick,
        clear: selection.clear,
        pressHandlers: {
            onMouseDown: keepShiftClickFocus,
            onPointerDownCapture: (event: MouseEvent<HTMLElement>) => {
                if (isSelectPressOnControl(event)) {
                    event.stopPropagation();
                }
            },
            // The press selects the card, so focus goes to it, not the control.
            onMouseDownCapture: (event: MouseEvent<HTMLElement>) => {
                if (isSelectPressOnControl(event)) {
                    event.stopPropagation();
                    event.preventDefault();
                    event.currentTarget.focus({ preventScroll: true });
                }
            },
            onClickCapture: (event: MouseEvent<HTMLElement>) => {
                if (isSelectPressOnControl(event)) {
                    event.stopPropagation();
                    selectFromClick(event);
                }
            },
        },
    };
};

/** Whether a slot's item is selected, for `BoardCardContext.isSelected`. */
export const useIsBoardItemSelected = (itemId: string) =>
    useContext(BoardSelectionContext)?.isItemSelected(itemId) ?? false;

const isOnCard = (target: EventTarget) =>
    target instanceof Element &&
    target.closest(`[${BOARD_CARD_ATTRIBUTE}]`) !== null;

/**
 * The board root's part: handlers where Escape (while no drag is on) and a
 * click on empty board space clear the selection, and the id of the hidden
 * "Selected" text to render. Events from portals rendered inside a card,
 * such as a picker's popup, aren't the board's.
 */
export const useBoardSelectionRoot = ({
    isDragActive,
    onKeyDown,
}: {
    isDragActive: boolean;
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
}) => {
    const selection = useContext(BoardSelectionContext);
    const handlers = {
        onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
            onKeyDown(event);
            if (
                selection !== null &&
                event.key === "Escape" &&
                !isDragActive &&
                !event.defaultPrevented &&
                !isFromPortal(event)
            ) {
                selection.clear();
            }
        },
        onClick: (event: MouseEvent<HTMLElement>) => {
            if (
                selection !== null &&
                !isOnCard(event.target) &&
                !isFromCardControl(event)
            ) {
                selection.clear();
            }
        },
    };
    return { handlers, describedById: selection?.describedById ?? null };
};
